<template>
  <div class="sidebar-wrapper">
    <!-- Logo 区（保持原样）-->
    <div class="topnav-logo"></div>

    <!-- 菜单滚动区 -->
    <el-scrollbar class="scrollbar">
      <el-menu
        ref="menuRef"
        :default-active="activeMenu"
        class="sidebar-menu"
        :router="true"
        background-color="transparent"
        text-color="#475569"
        active-text-color="#ffffff"
      >
        <div class="menu-section-label">NAVIGATION</div>
        <el-menu-item
          v-for="item in menuItems"
          :key="item.index"
          :index="item.index"
          :route="{ name: item.index }"
          :disabled="!!item.disabled"
          :title="item.disabled ? '待开发' : undefined"
          class="menu-item"
        >
          <el-icon class="menu-icon">
            <component :is="item.icon" />
          </el-icon>
          <template #title>
            <span class="menu-label">{{ item.label }}</span>
          </template>
        </el-menu-item>
      </el-menu>
    </el-scrollbar>

    <!-- 底部装饰区（始终固定在侧栏底部）-->
    <div class="sidebar-footer">
      <div class="footer-line"></div>
      <div class="footer-content">
        <div class="footer-brand">
          <span class="brand-dot"></span>
          <span class="brand-text">Racern IoT Platform</span>
        </div>
        <div class="footer-version">v1.0.0 · 管理端</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, markRaw } from "vue";
import { useRoute } from "vue-router";
import {
  Odometer,
  OfficeBuilding,
  Ship,
  List,
  Key,
  Document,
  Lock,
  Coin,
  // FolderOpened, // 归档管理入口暂隐藏，恢复时取消注释（含下方菜单项）
} from "@element-plus/icons-vue";
import type { Component } from "vue";

const route = useRoute();

// 菜单控件
const activeMenu = ref<string>("");
const updateActiveMenu = () => {
  activeMenu.value = route.name as string;
};
updateActiveMenu();
watch(() => route.name, updateActiveMenu);

// 菜单配置（markRaw 避免 Vue 对图标组件做不必要的响应式包装）
interface MenuItem {
  index: string;
  label: string;
  icon: Component;
  /** 待开发禁用：置灰不可点（悬浮提示"待开发"），功能就绪后移除即可恢复 */
  disabled?: boolean;
}

const menuItems = ref<MenuItem[]>([
  { index: "overall", label: "信息总览", icon: markRaw(Odometer) },
  {
    index: "organizationList",
    label: "集团列表",
    icon: markRaw(OfficeBuilding),
  },
  { index: "shipList", label: "船舶列表", icon: markRaw(Ship) },
  { index: "upgradeList", label: "推送记录", icon: markRaw(List) },
  {
    index: "generateEndpointConnector",
    label: "生成三元组",
    icon: markRaw(Key),
  },
  { index: "ccsReport", label: "报文发送", icon: markRaw(Document) },
  {
    index: "certBatchManagement",
    label: "证书与终端",
    icon: markRaw(Lock),
  },
  {
    index: "exchangeRate",
    label: "汇率管理",
    icon: markRaw(Coin),
    // 入口禁用（待开发）：需要恢复"不可点击"时取消下行注释即可
    // disabled: true,
  },
  // 归档管理入口暂隐藏：需要展示时取消下方注释（同时恢复 FolderOpened 导入）
  // {
  //   index: "archiveManagement",
  //   label: "归档管理",
  //   icon: markRaw(FolderOpened),
  // },
  // 账号管理入口在右上角头像下拉框中（不占侧边栏）
]);
</script>

<style lang="less" scoped>
// ===== 整体 wrapper：flex 纵向布局 =====
.sidebar-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  // 由两层渐变叠加，营造"高级纸张"质感
  background: radial-gradient(
      120% 80% at 0% 0%,
      rgba(8, 145, 178, 0.05) 0%,
      transparent 60%
    ),
    linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
}

// ===== 菜单滚动区：占满剩余空间 =====
.scrollbar {
  flex: 1;
  min-height: 0;
}

// ===== 菜单本体 =====
.sidebar-menu {
  height: 100%;
  border-right: 0;
  background: transparent !important;
  padding: 4px 12px 12px;
  overflow-x: hidden;

  // 分类小标签
  :deep(.menu-section-label) {
    display: block;
    padding: 14px 12px 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    color: #94a3b8;
  }

  // 单个菜单项
  :deep(.el-menu-item) {
    background: transparent !important;
    color: #475569 !important;
    height: 42px;
    line-height: 42px;
    margin: 3px 0;
    padding: 0 14px !important;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.3px;
    position: relative;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    // 激活态：左侧的圆角指示条
    &::before {
      content: "";
      position: absolute;
      left: 4px;
      top: 50%;
      transform: translateY(-50%) scaleY(0);
      width: 3px;
      height: 18px;
      border-radius: 2px;
      background: linear-gradient(180deg, #06b6d4 0%, #0891b2 100%);
      transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    // 图标
    .menu-icon {
      font-size: 17px;
      color: #64748b;
      margin-right: 12px;
      transition: color 0.25s ease, transform 0.25s ease;
    }

    .menu-label {
      transition: color 0.25s ease;
    }

    // 悬停态
    &:hover {
      background: rgba(8, 145, 178, 0.06) !important;
      color: #0e7490 !important;
      transform: translateX(2px);

      .menu-icon {
        color: #0891b2;
        transform: scale(1.08);
      }
    }

    // 激活态：青蓝渐变背景 + 白色文字 + 光晕
    &.is-active {
      background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%) !important;
      color: #ffffff !important;
      font-weight: 600;
      box-shadow: 0 4px 14px rgba(8, 145, 178, 0.32),
        0 1px 2px rgba(8, 145, 178, 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 0.15);
      transform: translateX(0);

      &::before {
        transform: translateY(-50%) scaleY(1);
        background: #ffffff;
      }

      .menu-icon {
        color: #ffffff !important;
      }

      .menu-label {
        color: #ffffff;
        letter-spacing: 0.4px;
      }
    }
  }

  :deep(.el-menu-item .el-icon) {
    color: inherit;
  }

  // 待开发禁用项：置灰 + not-allowed 光标，悬停不响应
  :deep(.el-menu-item.is-disabled) {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: transparent !important;
      color: #475569 !important;
      transform: none;

      .menu-icon {
        color: #64748b;
        transform: none;
      }
    }
  }
}

// ===== 底部装饰区域：始终固定在底部 =====
.sidebar-footer {
  flex-shrink: 0;
  padding: 14px 18px 14px;
  position: relative;

  .footer-line {
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(148, 163, 184, 0.25) 50%,
      transparent 100%
    );
    margin-bottom: 12px;
  }

  .footer-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-brand {
    display: flex;
    align-items: center;
    gap: 8px;

    .brand-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: linear-gradient(135deg, #06b6d4, #0891b2);
      box-shadow: 0 0 6px rgba(8, 145, 178, 0.6);
    }

    .brand-text {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.6px;
      background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  .footer-version {
    font-size: 11px;
    color: #94a3b8;
    letter-spacing: 0.5px;
    padding-left: 14px;
  }
}

.flex-grow {
  flex-grow: 1;
}

// ===== Logo 区（保持原样）=====
.topnav-logo {
  flex-shrink: 0;
  background-image: url("@/assets/logo.png");
  background-repeat: no-repeat;
  background-size: 70%;
  background-position: center;
  background-color: #ffffff;
  height: 56px;
  width: 100%;
  border-bottom: 1px solid rgba(8, 145, 178, 0.1);
  position: relative;
  // 顶部加一道细的青蓝高光线，让 logo 与下方菜单衔接得更精致
  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(8, 145, 178, 0.35) 50%,
      transparent 100%
    );
  }
}
</style>
